import { IUpdateDraftPayload } from "../services/serviceRequestCreation";
import { IAnswerMap, IQuestion } from "../types";

/**
 * The keys a screen's form may hold that are draft fields rather than answers.
 *
 * The address screen writes lat/lng/zip through `setValue` under these exact
 * names because that is what the PUT expects them to be called. No config
 * question uses any of them as a question key, so pulling them out of the form
 * values cannot swallow a real answer.
 */
const PAYLOAD_FIELD_KEYS = [
  "latitude",
  "longitude",
  "zip_code",
  "place_name",
] as const;

/**
 * The visibility screen's question, and the option that means "pros only".
 *
 * Derived from nothing — `single_select` is far too common a type to key off,
 * so this is the one place in the flow that names a question key. Both strings
 * come from the config's `audience` question (`fixtures/*.json`).
 */
const AUDIENCE_QUESTION_KEY = "audience";
const CERTIFIED_ONLY = "certified_only";

/**
 * Splits a screen's submitted values into the two halves the PUT wants.
 *
 * The address is a draft field: `IUpdateDraftPayload` carries `location` and
 * its geo siblings at the top level, next to `answers`. Rather than have every
 * screen know that, the split is derived — from the question's `type` for the
 * address itself, and from the field names above for the geo details. It is
 * also kept as an answer, for the reason given on the branch below.
 */
export const toDraftFields = (
  questions: IQuestion[],
  values: IAnswerMap
): {
  answers: IAnswerMap;
  fields: Partial<Omit<IUpdateDraftPayload, "answers">>;
} => {
  const answers: IAnswerMap = { ...values };
  const fields: Partial<Omit<IUpdateDraftPayload, "answers">> = {};

  questions.forEach((question) => {
    // The address goes out *both* ways, like `audience` below. It is a draft
    // field — `location` and its geo siblings — but publishing screens every
    // required question against the answers array
    // (`ScreenDraft#unanswered_required_questions`), and the address question
    // is `required`, so an address kept only in `location` reads as unanswered
    // and `validate: true` answers 422 `missing_required_answers`.
    //
    // TODO(RT-4321, backend): `ScreenDraft` already checks `location`,
    // `latitude` and `longitude` through `REQUIRED_FIELDS`. It should skip
    // address-type questions in the answers check instead of making the
    // frontend send the same value twice.
    if (question.type === "address") {
      fields.location = String(values[question.key] ?? "");
    }

    // Photo ids are a draft field of their own (`photo_attachments`), not an
    // answer — same reasoning as `address` above.
    if (question.type === "picture_upload") {
      fields.photo_attachments = (values[question.key] ?? []) as number[];
      delete answers[question.key];
    }

    // `audience` is the one question the PUT also wants as a flag of its own.
    // The answer is *kept* as well: it is what `isScreenComplete` looks at when
    // the flow is rebuilt from a reloaded draft, and unlike `location` the
    // response has nowhere else to carry it back from.
    if (question.key === AUDIENCE_QUESTION_KEY) {
      fields.pro_only = values[question.key] === CERTIFIED_ONLY;
    }
  });

  PAYLOAD_FIELD_KEYS.forEach((key) => {
    if (!(key in answers)) return;

    const value = answers[key];
    delete answers[key];
    if (value == null) return;

    if (key === "latitude" || key === "longitude") fields[key] = Number(value);
    else fields[key] = String(value);
  });

  return { answers, fields };
};
